/**
 * ReportAttackAnalysis.js v2
 * - Bar chart: each attack type as individual bar, colored by severity
 * - Pie chart: clean with no overlapping labels, proper legend below
 * - MITRE table: severity from severityFromLabel(), not index-based
 */
import React from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  Cell, PieChart, Pie, CartesianGrid
} from "recharts";
import { getMitre, severityFromLabel } from "../reportUtils";

// Clean label — strip encoding artifacts
const cleanLabel = (s) =>
  s.replace(/[^\x00-\x7F]/g, "").replace(/\s+/g, " ").trim() || s.trim();

export default function ReportAttackAnalysis({ stats, theme, SEVERITY_COLORS: SC }) {
  if (!stats || !stats.attackDist.length) return null;

  const palette = theme.colors.chartColors || Object.values(SC);

  // Each attack type gets its own severity color
  const top15 = stats.attackDist.slice(0, 15).map(a => ({
    ...a,
    label:    cleanLabel(a.name),
    sev:      severityFromLabel(a.name),
    color:    SC[severityFromLabel(a.name)] || SC.info,
    pct:      ((a.value / stats.total) * 100).toFixed(1),
    mitre:    getMitre(a.name),
  }));

  // Severity pie — only non-zero tiers
  const sevData = [
    { name: "Critical", value: stats.critical, color: palette[0 % palette.length] },
    { name: "High",     value: stats.high,     color: palette[1 % palette.length] },
    { name: "Medium",   value: stats.medium,   color: palette[2 % palette.length] },
    { name: "Low",      value: stats.low,      color: palette[3 % palette.length] },
    { name: "Info",     value: stats.info,     color: palette[4 % palette.length] },
  ].filter(d => d.value > 0);

  // Total non-benign attacks
  const totalAttacks = stats.critical + stats.high + stats.medium + stats.low;

  return (
    <div className="report-section page-break" style={{ marginBottom: 24 }}>
      <SectionTitle color="#C62828">Attack Analysis</SectionTitle>

      {/* Row 1: Bar chart + Pie chart */}
      <div style={{ display: "grid", gridTemplateColumns: "3fr 2fr", gap: 16, marginBottom: 16 }}>

        {/* ── Top Attack Types Bar Chart ── */}
        <div className="report-card chart-container" style={cardStyle(theme)}>
          <SubTitle theme={theme}>TOP ATTACK TYPES — BY EVENT COUNT</SubTitle>
          <div style={{ fontFamily: "monospace", fontSize: 11, color: theme.colors.textMuted, marginBottom: 12 }}>
            Each bar colored by severity — {top15.length} attack types detected
          </div>
          <ResponsiveContainer width="100%" height={Math.max(300, top15.length * 36)}>
            <BarChart
              data={top15}
              layout="vertical"
              margin={{ left: 10, right: 60, top: 4, bottom: 4 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.cardBorder} horizontal={false} />
              <XAxis
                type="number"
                tick={{ fontSize: 11, fill: theme.colors.textMuted }}
                tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}K` : v}
              />
              <YAxis
                type="category"
                dataKey="label"
                width={200}
                tick={{ fontSize: 11, fill: theme.colors.textSecondary }}
              />
              <Tooltip
                contentStyle={{ background: theme.colors.card, border: `1px solid ${theme.colors.cardBorder}`, fontFamily: "monospace", fontSize: 11 }}
                formatter={(v, name, props) => [
                  `${v.toLocaleString()} events (${props.payload.pct}%)`,
                  props.payload.label
                ]}
                labelFormatter={() => ""}
              />
              <Bar dataKey="value" radius={[0, 4, 4, 0]} label={{
                position: "right",
                style: { fontFamily: "monospace", fontSize: 10, fill: theme.colors.textMuted },
                formatter: (v) => v.toLocaleString()
              }}>
                {top15.map((d, i) => (
                  <Cell key={i} fill={d.color} fillOpacity={0.85} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>

          {/* Severity color legend */}
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 10, paddingTop: 10, borderTop: "1px solid #F0F0F0" }}>
            {Object.entries(SC).filter(([k]) => ["critical","high","medium","low","info"].includes(k)).map(([k, c]) => (
              <div key={k} style={{ display: "flex", alignItems: "center", gap: 5, fontFamily: "monospace", fontSize: 10, color: theme.colors.textMuted }}>
                <div style={{ width: 12, height: 12, borderRadius: 2, background: c }} />
                {k.toUpperCase()}
              </div>
            ))}
          </div>
        </div>

        {/* ── Severity Breakdown Pie ── */}
        <div className="report-card chart-container" style={cardStyle(theme)}>
          <SubTitle theme={theme}>SEVERITY BREAKDOWN</SubTitle>

          {/* Stats row */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
            {sevData.map(d => (
              <div key={d.name} style={{ flex: 1, minWidth: 60, padding: "8px 10px", background: `${d.color}10`, border: `1px solid ${d.color}33`, borderRadius: 6, textAlign: "center" }}>
                <div style={{ fontFamily: "monospace", fontSize: 16, fontWeight: 900, color: d.color }}>{d.value.toLocaleString()}</div>
                <div style={{ fontFamily: "monospace", fontSize: 9, color: theme.colors.textMuted, letterSpacing: 1 }}>{d.name.toUpperCase()}</div>
              </div>
            ))}
          </div>

          {/* Pie — no inline labels to avoid overlap */}
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={sevData}
                dataKey="value"
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={85}
                paddingAngle={2}
              >
                {sevData.map((d, i) => <Cell key={i} fill={d.color} />)}
              </Pie>
              <Tooltip
                contentStyle={{ background: theme.colors.card, border: `1px solid ${theme.colors.cardBorder}`, fontFamily: "monospace", fontSize: 11 }}
                formatter={(v, name) => [`${v.toLocaleString()} (${((v/stats.total)*100).toFixed(1)}%)`, name]}
              />
            </PieChart>
          </ResponsiveContainer>

          {/* Clean legend below pie — not overlapping */}
          <div style={{ marginTop: 8 }}>
            {sevData.map(d => {
              const pct = ((d.value / stats.total) * 100).toFixed(1);
              const barW = Math.max(2, Math.round((d.value / stats.total) * 100));
              return (
                <div key={d.name} style={{ marginBottom: 6 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontFamily: "monospace", fontSize: 11, marginBottom: 2 }}>
                    <span style={{ color: d.color, fontWeight: 700 }}>● {d.name}</span>
                    <span style={{ color: theme.colors.textPrimary, fontWeight: 700 }}>{d.value.toLocaleString()} <span style={{ color: theme.colors.textLabel }}>({pct}%)</span></span>
                  </div>
                  <div style={{ height: 4, background: theme.colors.cardBorder, borderRadius: 2 }}>
                    <div style={{ height: 4, width: `${barW}%`, background: d.color, borderRadius: 2 }} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Attack summary */}
          {totalAttacks > 0 && (
            <div style={{ marginTop: 12, padding: "10px 12px", background: `${SC.critical}08`, border: `1px solid ${SC.critical}22`, borderRadius: 6 }}>
              <div style={{ fontFamily: "monospace", fontSize: 11, color: SC.critical, fontWeight: 700 }}>
                {totalAttacks.toLocaleString()} confirmed attack events
              </div>
              <div style={{ fontFamily: "monospace", fontSize: 10, color: theme.colors.textMuted, marginTop: 2 }}>
                {((totalAttacks / stats.total) * 100).toFixed(2)}% of total traffic · {stats.info.toLocaleString()} benign
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Row 2: Attack detail table with MITRE */}
      <div className="report-card" style={cardStyle(theme)}>
        <SubTitle theme={theme}>ATTACK TYPE DETAIL — MITRE ATT&CK MAPPING</SubTitle>
        <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "monospace", fontSize: 11 }}>
          <thead>
            <tr style={{ background: theme.colors.tableHeader }}>
              {["#","Attack Type","Events","%","Severity","MITRE Tactic","Technique","Description"].map(h => (
                <th key={h} style={{ padding: "8px 10px", textAlign: "left", color: theme.colors.textPrimary, borderBottom: `2px solid ${theme.colors.cardBorder}`, fontSize: 10, letterSpacing: 1, whiteSpace: "nowrap" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {top15.map((a, i) => (
              <tr key={i} style={{ background: i%2===0 ? theme.colors.rowAlt : theme.colors.card, borderBottom: `1px solid ${theme.colors.cardBorder}` }}>
                <td style={{ padding: "7px 10px", color: theme.colors.textMuted }}>{i+1}</td>
                <td style={{ padding: "7px 10px", color: theme.colors.textPrimary, fontWeight: 700 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: a.color, flexShrink: 0 }} />
                    {a.label}
                  </div>
                </td>
                <td style={{ padding: "7px 10px", color: theme.colors.textPrimary, fontWeight: 700 }}>{a.value.toLocaleString()}</td>
                <td style={{ padding: "7px 10px" }}>
                  <div style={{ fontFamily: "monospace", fontSize: 11, color: theme.colors.textMuted }}>{a.pct}%</div>
                  <div style={{ height: 3, width: `${Math.max(4, parseFloat(a.pct))}%`, background: a.color, borderRadius: 2, marginTop: 2, maxWidth: 80 }} />
                </td>
                <td style={{ padding: "7px 10px" }}>
                  <span style={{ padding: "2px 7px", background: `${a.color}18`, border: `1px solid ${a.color}44`, borderRadius: 4, color: a.color, fontSize: 10, fontWeight: 700, whiteSpace: "nowrap" }}>
                    {a.sev.toUpperCase()}
                  </span>
                </td>
                <td style={{ padding: "7px 10px", color: theme.colors.accent, fontSize: 11 }}>{a.mitre.tactic}</td>
                <td style={{ padding: "7px 10px" }}>
                  <span style={{ padding: "2px 6px", background: `${theme.colors.accent}18`, border: "1px solid #9C27B020", borderRadius: 4, color: theme.colors.accent, fontSize: 10, fontWeight: 700 }}>
                    {a.mitre.technique}
                  </span>
                </td>
                <td style={{ padding: "7px 10px", color: theme.colors.textMuted, fontSize: 10 }}>{a.mitre.name}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const cardStyle = (theme) => ({
  background: theme.colors.card,
  border: `1px solid ${theme.colors.cardBorder}`,
  borderRadius: 10, padding: 20,
});
const SectionTitle = ({ children, color }) => (
  <div style={{ fontFamily:"monospace", fontSize:13, fontWeight:900, color, letterSpacing:2, textTransform:"uppercase", borderLeft:`4px solid ${color}`, paddingLeft:12, marginBottom:16 }}>{children}</div>
);
const SubTitle = ({ children, theme }) => (
  <div style={{ fontFamily:"monospace", fontSize:11, color:theme.colors.textMuted, letterSpacing:1, marginBottom:10, fontWeight:700 }}>{children}</div>
);
